import { ChatPrizeStatus } from '@/components/chat/_domain';
import { injectable, inject } from 'inversify';
import { Lang } from 'vuejs-localization';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { ConfigurationPaths, IConfigurationService, ConfigurationKey } from '@/app-modules/config';
import { ILocalisationService } from '@/app-modules/localisation-service';
import { ISweetAlertService } from '@/app-modules/sweet-alert-service';
import { IChatServerService } from '@/app-modules/server-service';
import { ChatPrizeResponse } from '@/components/chat/_domain/server';
import { ISubscription } from 'rxjs/Subscription';
import { IPersistedStore } from '@/app-modules/store/persisted-store';
import { container } from '@/app-modules/_ioc';
import { User } from '@/app-modules/store/modules/user';
import { IHttpRequest, IHttpService } from '@/app-modules/http';
import { base64 } from '@/app-modules/utils';
import { PrizeHelper } from '@/components/game/_domain';
import { ITogglesService } from '@/app-modules/config';


interface IPromoPrizeUser {
  username: string;
  cmUsername: string;
}

interface IChatPrizeService {
  awardPlayerChatPrize(usernames: string[], chatPrizeStatus: ChatPrizeStatus, server: IChatServerService): Promise<void>;
  showChatPrizeSuccessOrFailureMessage(chatPrizeStatus: ChatPrizeStatus, username: string): void;
  awardPlayerPromoPrize(username: string): Promise<void>;
}

@injectable()
class ChatPrizeService implements IChatPrizeService {
  private readonly $lang: Lang;
  public prizeHelper: PrizeHelper = new PrizeHelper();

  constructor(
    @inject(SERVICE_IDENTIFIERS.IConfigurationService) private readonly configurationService: IConfigurationService,
    @inject(SERVICE_IDENTIFIERS.ILocalisationService) private readonly localisationService: ILocalisationService,
    @inject(SERVICE_IDENTIFIERS.ISweetAlertService) private readonly sweetAlertService: ISweetAlertService,
    @inject(SERVICE_IDENTIFIERS.IHttpService) private readonly httpService: IHttpService,
    @inject(SERVICE_IDENTIFIERS.ITogglesService) private readonly toggleService: ITogglesService
  ) {
    this.$lang = this.localisationService.getLangInstance();
  }

  public async awardPlayerPromoPrize(username: string) {
    const persistedStore = container.get(SERVICE_IDENTIFIERS.IPersistedStore) as IPersistedStore;
    const cmUser = persistedStore.Get(User);
    const request = this.buildPromoPrizeRequest(username, cmUser.username);
    if (username) {
      const httpRequest = this.getHttpRequestForPromoPrizeRequest(request, cmUser);
      const useNewEndPoint = await this.toggleService.isLive('CIC_340_UseNetPromoPrizeEndpoint');
      const response = await this.httpService.post(httpRequest, useNewEndPoint);
      let promoAwardSuccess = false;
      if (useNewEndPoint) {
        promoAwardSuccess = (response.status === 200 && response.data.status === 200) ? true : false;
      } else {
        promoAwardSuccess = (response.status === 200 && response.data.Status === 200) ? true : false;
      }

      this.showPromoPrizeSuccessOrFailureMessage(promoAwardSuccess, username);
    }
  }

  public buildPromoPrizeRequest(username: string, cmUsername: string): IPromoPrizeUser {
    const request = { username, cmUsername };
    return request;
  }

  public async awardPlayerChatPrize(usernames: string[], chatPrizeStatus: ChatPrizeStatus, server: IChatServerService): Promise<void> {
    const chatPrizeValue: number = await this.getPrizeAmount();

    if (isNaN(chatPrizeValue)) return;

    if (usernames && usernames.length === 1) {
      return this.awardPlayerChatPrizeForSinglePlayer(usernames[0], chatPrizeStatus, chatPrizeValue, server);
    }
    return this.awardPlayerChatPrizeForMultiplePlayers(usernames, chatPrizeValue, server);
  }

  private async awardPlayerChatPrizeForSinglePlayer(username: string, chatPrizeStatus: ChatPrizeStatus, chatPrizeValue: number, server: IChatServerService): Promise<void> {
    const title: string = this.$lang.chat.prize.title;
    const confirmationMessage = this.formatMessageForUser(this.formatMessageForPrize(this.$lang.chat.prize.confirm, this.prizeHelper.formatPrize(chatPrizeValue)), username);
    const willAwardChatPrize = await this.showConfirmationMessageForUser(confirmationMessage, title, [username]);
    if (willAwardChatPrize) {
      chatPrizeStatus = this.initialiseChatPrizeStatusForUser(chatPrizeStatus, username);
      const subscription = this.subscribeToChatPrizeResponsesForUser(server, chatPrizeStatus, username);
      await this.sendChatPrizeCommandForUser(username, chatPrizeValue, server);
      this.autoUnsubscribeFromChatPrizeResponses(subscription);
    }
  }

  private initialiseChatPrizeStatusForUser(chatPrizeStatus: ChatPrizeStatus, username: string): ChatPrizeStatus {
    if (!chatPrizeStatus) {
      chatPrizeStatus = new ChatPrizeStatus();
    }
    chatPrizeStatus.username = username;
    return chatPrizeStatus;
  }

  private subscribeToChatPrizeResponsesForUser(server: IChatServerService, chatPrizeStatus: ChatPrizeStatus, username: string): ISubscription {
    const subscription = server.subscribeChatPrize((payload: ChatPrizeResponse) => {
      chatPrizeStatus.chatPrizeSuccess = payload.success;
      chatPrizeStatus.responseMessage = payload.errorMessage;
      this.showChatPrizeSuccessOrFailureMessage(chatPrizeStatus, username);
      subscription.unsubscribe();
    });
    return subscription;
  }

  private autoUnsubscribeFromChatPrizeResponses(subscription: ISubscription): void {
    const chatServerResonseTimeoutMilliseconds = this.getChatServerResponseTimeoutInMilliseconds();
    window.setTimeout(() => {
      if (!subscription.closed) {
        subscription.unsubscribe();
      }
    }, chatServerResonseTimeoutMilliseconds);
  }

  private getChatServerResponseTimeoutInMilliseconds(): number {
    return Number(this.configurationService.getConfigurationItem(ConfigurationKey.CHAT_WEBSOCKET_CONNECTION_LOST_DETECTION_TIMEOUT_MILLISECONDS));
  }

  private async awardPlayerChatPrizeForMultiplePlayers(usernames: string[], chatPrizeValue: number, server: IChatServerService): Promise<void> {
    if (usernames && usernames.length > 0) {
      const title: string = this.$lang.chat.prize.title;
      const confirmationMessage: string = this.formatMessageForPrize(this.$lang.chat.prize.confirmMultiple, this.prizeHelper.formatPrize(chatPrizeValue));
      const willAwardChatPrize = await this.showConfirmationMessageForUser(confirmationMessage, title, usernames);
      if (willAwardChatPrize) {
        for (const username of usernames) {
          await this.sendChatPrizeCommandForUser(username, chatPrizeValue, server);
        }
      }
    }
  }

  public showChatPrizeSuccessOrFailureMessage(chatPrizeStatus: ChatPrizeStatus, username: string): void {
    const title: string = this.$lang.chat.prize.title;
    const successMessage = this.formatMessageForUser(this.$lang.chat.prize.success, username);
    const failureMessage = this.formatMessageForUser(`${this.$lang.chat.prize.failed} ${chatPrizeStatus.responseMessage}.`, username);
    this.showSuccessOrFailureMessageForUser(chatPrizeStatus.chatPrizeSuccess, successMessage, failureMessage, title);
  }

  public showPromoPrizeSuccessOrFailureMessage(promoAwardSuccess: boolean, username: string): void {
    const title: string = this.$lang.chat.promoPrize.title;
    const successMessage = this.formatMessageForUser(this.$lang.chat.promoPrize.success, username);
    const failureMessage = this.formatMessageForUser(`${this.$lang.chat.promoPrize.failed}.`, username);
    this.showSuccessOrFailureMessageForUser(promoAwardSuccess, successMessage, failureMessage, title);
  }

  private async sendChatPrizeCommandForUser(username: string, chatPrizeValue: number, server: IChatServerService) {
    const adminCommand = {
      toPayload() {
        return `<admin command="chatprize" username="${username}" prize="${chatPrizeValue}" />`;
      }
    };
    server.send(adminCommand);
  }

  private async getChatPrizeValue(): Promise<string> {
    const config = await this.configurationService.getConfiguration();
    return config.getConfigurationByPath(ConfigurationPaths.prizes.chat);
  }

  private async getMappedChatPrizeObject(prizes: string): Promise<{ [val: string]: string }> {
    const values = prizes.split(',').map((item) => parseInt(item.trim(), 10));
    return values.reduce((o: object, k) => ({ ...o, [k]: this.prizeHelper.formatPrize(k) }), {});
  }

  private async getPrizeAmount(): Promise<number> {
    const prizes = await this.getChatPrizeValue();
    const splitPrizes = prizes.split(',');
    if (splitPrizes.length === 1) {
      return parseInt(splitPrizes[0], 10);
    }
    const value = await this.sweetAlertService.show({
      title: this.$lang.chat.prize.prizeTitle,
      text: this.$lang.chat.prize.prize,
      showCloseButton: true,
      input: 'radio',
      inputValue: splitPrizes[0],
      inputOptions: await this.getMappedChatPrizeObject(prizes),
      inputValidator: (val) => {
        if (!val) {
          return 'You need to choose something!';
        }
      }
    });

    return parseInt(value.value, 10);
  }


  private async showConfirmationMessageForUser(messagePopupText: string, messagePopupTitleText: string, usernames: string[]): Promise<boolean> {
    // We shouldn't be able to Award the JackpotAlert user!
    if (!usernames.includes(this.$lang.chat.message.jackpotAlert)) {
      const refocus = true;
      const { value: result } = await this.sweetAlertService.show({
        title: messagePopupTitleText,
        text: messagePopupText,
        type: 'warning',
        allowOutsideClick: false,
        focusConfirm: false,
        showConfirmButton: true,
        showCancelButton: true
      }, refocus);
      return result;
    }
    return false;
  }

  private showSuccessOrFailureMessageForUser(success: boolean, successMessage: string, failureMessage: string, messagePopupTitleText: string) {
    const refocus = true;
    this.sweetAlertService.show({
      title: messagePopupTitleText,
      text: success ? successMessage : failureMessage,
      type: success ? 'success' : 'error'
    }, refocus);
  }

  private formatMessageForUser(message: string, username: string): string {
    return message.format({ username });
  }

  private formatMessageForPrize(message: string, chatPrizeValue: string): string {
    return message.format({ chatPrizeValue });
  }

  private getHttpRequestForPromoPrizeRequest(request: IPromoPrizeUser, cmUser: User): IHttpRequest {
    const encodedAuth = base64.Encode(`${cmUser.userId}:${cmUser.crosscheckId}`);
    const httpRequest: IHttpRequest = {
      Url: '/promo-prize',
      XChatModsAuthorization: encodedAuth,
      Payload: request
    };
    return httpRequest;
  }
}

export { IChatPrizeService, ChatPrizeService };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/chat/_domain/modules/chat-prize-service/chat-prize-service.ts